
import { supabase } from '@/integrations/supabase/client';
import { UserProfile, Organization, UserOrganizationMembership } from './types';

export const fetchUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    console.log('UserDataService: Fetching user profile for:', userId);

    const { data: profileData, error: profileError } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (profileError) {
      console.error('UserDataService: Error fetching profile:', profileError);
      return null;
    }

    console.log('UserDataService: Profile fetched successfully');
    
    // Transform the data to match UserProfile interface
    // Note: role and organization_id are not stored in user_profiles anymore
    // They come from user_organization_memberships
    return {
      id: profileData.id,
      user_id: profileData.user_id,
      full_name: profileData.full_name,
      is_active: profileData.is_active,
      last_login_at: profileData.last_login_at,
      created_at: profileData.created_at,
      updated_at: profileData.updated_at,
      role: 'staff', // Default role, will be overridden by organization membership
      organization_id: '', // Will be set by current organization
    };
  } catch (error) {
    console.error('UserDataService: Error fetching user profile:', error);
    return null;
  }
};

export const fetchUserOrganizations = async (userId: string): Promise<UserOrganizationMembership[]> => {
  try {
    console.log('UserDataService: Fetching user organizations for:', userId);

    const { data, error } = await supabase.rpc('get_user_organizations', {
      p_user_id: userId
    });

    if (error) {
      console.error('UserDataService: Error fetching user organizations:', error);
      return [];
    }

    // Transform the data to match UserOrganizationMembership interface
    const memberships = data?.map(profile => ({
      organization_id: profile.organization_id,
      organization_name: profile.organization_name ?? '',
      organization_slug: profile.organization_slug ?? '',
      user_role: profile.user_role,
      is_active: profile.is_active,
      joined_at: profile.joined_at
    })) ?? [];

    console.log('UserDataService: User organizations fetched successfully');
    return memberships;
  } catch (error: unknown) {
    console.error('UserDataService: Error fetching user organizations:', error);
    return [];
  }
};

export const fetchOrganization = async (organizationId: string, bypassCache: boolean = false): Promise<Organization | null> => {
  try {
    console.log('UserDataService: Fetching organization for ID:', organizationId);

    // Check cache first (unless bypassed)
    const { organizationCache } = await import('@/utils/organizationCache');
    if (!bypassCache) {
      const cachedOrg = organizationCache.get(organizationId);
      if (cachedOrg) {
        console.log('UserDataService: Using cached organization data');
        return cachedOrg;
      }
    } else {
      // Ensure we don't use stale data if bypassing
      organizationCache.invalidate?.(organizationId);
    }

    // If not cached, fetch from database using the comprehensive RPC
    const { data, error } = await supabase
      .rpc('get_organization_info', { p_organization_id: organizationId });

    if (error) {
      console.error('UserDataService: Error fetching organization info:', error);
      return null;
    }

    const orgInfo = data?.[0];
    if (!orgInfo) {
      console.error('UserDataService: No organization info returned');
      return null;
    }

    // Transform to match Organization interface
    const organization: Organization = {
      id: orgInfo.organization_id,
      name: orgInfo.organization_name || '',
      slug: orgInfo.organization_slug || '',
      logo_light_url: orgInfo.logo_light_url,
      logo_dark_url: orgInfo.logo_dark_url,
      terms_file_url: orgInfo.terms_file_url,
      notes: orgInfo.notes || '',
      created_at: orgInfo.created_at,
      updated_at: orgInfo.updated_at,
    };

    // Cache the result
    organizationCache.set(organizationId, organization);
    
    console.log('UserDataService: Organization fetched and cached successfully');
    return organization;
  } catch (error) {
    console.error('UserDataService: Error fetching organization:', error);
    return null;
  }
};

export const updateLastLogin = async (userId: string): Promise<void> => {
  try {
    await supabase
      .from('user_profiles')
      .update({ last_login_at: new Date().toISOString() })
      .eq('user_id', userId);
  } catch (error) {
    console.error('UserDataService: Error updating last login:', error);
  }
};

export const updateUserProfile = async (userId: string, updates: Partial<UserProfile>) => {
  try {
    const { error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('user_id', userId);

    return { error: error || null };
  } catch (error: unknown) {
    return { error: error instanceof Error ? error : new Error('Failed to update profile') };
  }
};
